import * as React from 'react';
import { Api } from '../../../../../typings/api';
import { Translate, I18n } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../../reducers';
import InnerPanel from '../InnerPanel';
import RadioBox, { RadioBoxTheme } from '../../../../Form/RadioBox';
import ButtonRemove from '../../ButtonRemove';
import {
	FileLabelWrapper,
	FileLabelInner,
	FileLabel,
	Icon,
	Label,
	PopupBody,
	CheckboxWrapper,
} from './styles';
import ResourceSelector from '../../../../../containers/ResourceSelector';
import { ResourceSelectorTheme } from '../../../../ResourceSelector';
import Portal from '../../../../Portal';
import Popup, { PopupTheme } from '../../../../Popup';
import Checkbox from '../../../../Form/Checkbox';
import { importPublicationPdf, savePublicationPdfOption } from '../../../../../containers/Editor/actions';
import { PdfImportingStatus, SETTINGS_PANEL_CLASSNAME } from '../../../../../containers/Editor/constants';
import { getPublicationPdfImportingId } from '../../../../../containers/Editor/utils/helpers';
import Utils from '../../../../../utils';

export interface PdfSettingsPanelProps {
	publicationId: number;
	pdfOption: Api.PublicationPdfOption;
	pdfImportingStatus: PdfImportingStatus;
	onClose: () => void;
	dispatch: Dispatch<StoreState>;
	globalId?: number; // id of publication used as a source for pdf
	label?: string;
}

const getRadioBoxLabel = (pdfOption: Api.PublicationPdfOption): string => {
	switch (pdfOption) {
		case Api.PublicationPdfOption.FULL:
			return 'messages.full_pdf';
		case Api.PublicationPdfOption.CUSTOM:
		default:
			return 'messages.custom_pdf';
	}
};

const getIconName = (globalId?: number | null): string => (
	globalId ? 'iconset_page-bold' : 'iconset_uprez_adobe_pdf'
);

const getImportingLabel = (status: PdfImportingStatus): string => {
	switch (status) {
		case PdfImportingStatus.IMPORTING:
			return 'messages.importing';
		case PdfImportingStatus.PROCESSING:
			return 'messages.processing';
		case PdfImportingStatus.REMOVING:
			return 'messages.removing';
		default:
			return '';
	}
};

const isValidResourceFromApp = ({ type }: Api.DriveItem): boolean => (
	type === Api.DriveItemType.Publication
);

const PdfSettingsPanel = ({
	publicationId,
	label,
	globalId,
	pdfOption: initialPdfOption,
	pdfImportingStatus,
	onClose,
	dispatch,
}: PdfSettingsPanelProps) => {
	const [pdfOption, setPdfOption] = React.useState(initialPdfOption);
	const [isPopupDisplayed, setIsPopupDisplayed] = React.useState(false);
	const [shouldUpdateThumbs, setShouldUpdateThumbs] = React.useState(false);
	const sourceFile = React.useRef<{ file: File, isCorrectFileType: boolean } | null>(null);
	const sourcePublicationId = React.useRef<number | null>(null);

	const importManagerId = React.useMemo(() => {
		return getPublicationPdfImportingId(publicationId, PdfImportingStatus.IMPORTING);
	}, [publicationId]);

	const handlePdfOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPdfOption(e.target.value as Api.PublicationPdfOption);
	};

	const handleFileSelect = (file: File, isCorrectFileType: boolean) => {
		setIsPopupDisplayed(true);
		sourceFile.current = { file, isCorrectFileType };
	};

	const handlePublicationSelect = ([{ id, label }]: Api.DriveItem[]) => {
		sourcePublicationId.current = id;
		dispatch(importPublicationPdf(publicationId, importManagerId, null, { id, label }, shouldUpdateThumbs, pdfOption));
	};

	const handlePopupConfirm = () => {
		dispatch(importPublicationPdf(publicationId, importManagerId, sourceFile.current, null, shouldUpdateThumbs, pdfOption));
		handlePopupClose();
	};

	const handlePopupClose = () => {
		setIsPopupDisplayed(false);
		setShouldUpdateThumbs(false);
		sourceFile.current = null;
	};

	const handleConfirm = () => {
		dispatch(savePublicationPdfOption(publicationId, pdfOption));
		onClose();
	};

	const handleButtonRemoveClick = () => {
		dispatch(importPublicationPdf(publicationId, importManagerId, null, { id: -1, label: '' }, shouldUpdateThumbs, pdfOption));
	};

	React.useEffect(() => {
		if (!pdfImportingStatus && sourcePublicationId.current) {
			sourcePublicationId.current = null;
		}
	}, [pdfImportingStatus]);

	return (
		<>
			<InnerPanel
				onConfirm={handleConfirm}
				onClose={onClose}
			>
				<ResourceSelector
					id={importManagerId}
					className={SETTINGS_PANEL_CLASSNAME}
					isValidResourceFromApp={isValidResourceFromApp}
					fileExtensions={['.pdf']}
					theme={ResourceSelectorTheme.DARK}
					customImportHandler={handleFileSelect}
					onSelect={handlePublicationSelect}
				/>
				{(label || pdfImportingStatus) &&
					<FileLabelWrapper>
						<FileLabelInner>
							<Icon>
								{Utils.getSvgIcon(getIconName(globalId || sourcePublicationId.current))}
							</Icon>
							<FileLabel isImporting={!!pdfImportingStatus}>
								{pdfImportingStatus
									? <Translate value={getImportingLabel(pdfImportingStatus)} />
									: label
								}
							</FileLabel>
						</FileLabelInner>
						{globalId &&
							<ButtonRemove
								onClick={handleButtonRemoveClick}
							/>
						}
					</FileLabelWrapper>
				}
				<Label value="messages.pdf_type" />
				{Object.values(Api.PublicationPdfOption).map(option => (
					<RadioBox
						key={option}
						name="pdfOption"
						label={<Translate value={getRadioBoxLabel(option)} />}
						value={option}
						isChecked={option === pdfOption}
						theme={RadioBoxTheme.DARK}
						onChange={handlePdfOptionChange}
					/>
				))}
			</InnerPanel>
			<Portal parentNode={document.body}>
				<Popup
					className={`prevent-drag ${SETTINGS_PANEL_CLASSNAME}`}
					isDisplayed={isPopupDisplayed}
					label={I18n.t('messages.update_pdf_thumb')}
					theme={PopupTheme.DARK}
					onConfirm={handlePopupConfirm}
					onCancel={handlePopupClose}
				>
					<PopupBody>
						<Translate value="messages.update_pdf_thumb_message" />
						{sourceFile.current &&
							<CheckboxWrapper>
								<Checkbox
									name="update-pdf-thumb-accept"
									label={<Translate value="messages.update_pdf_thumb_accept" />}
									isChecked={shouldUpdateThumbs}
									onChange={() => setShouldUpdateThumbs((isChecked) => !isChecked)}
								/>
							</CheckboxWrapper>
						}
					</PopupBody>
				</Popup>
			</Portal>
		</>
	);
};

export default PdfSettingsPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/SettingsPanel/PdfSettingsPanel/index.tsx